<script setup lang="ts">
// Components
import type { BarChartData } from '~/components/nrjx/chart/bar/index.vue';
import type {
  StandardFabricationOrderResponse,
  StandardOffProductionResponse,
  StandardProductionResponse,
  StandardResponse,
  StandardWeeklyResponse,
} from '../../types';
import dayjs from 'dayjs';
import { until } from '@vueuse/core';
import type { ConsumptionModalProps } from '~/components/modale/consumption.vue';

// Composables
const { options } = useStandardChart();

// Store
const { standardResponse, loading: loadingStandard, standardError, areaId, energyType } = storeToRefs(standardStore());

// Data
const datasets = ref<BarChartData[]>([]);
const loading = ref(true);
const open = ref(false);
const modalData = ref<ConsumptionModalProps | null>(null);

const handleClick = (index: number) => {
  const type = standardResponse.value?.type;

  if (type === 'weekly' || type === 'off_production') {
    const response = standardResponse.value!;
    const item = response?.items?.[index];
    if (!item || !areaId.value || !energyType.value) return;

    modalData.value = {
      start: item.event.start,
      end: item.event.end,
      type: type,
      areaId: areaId.value,
      energyType: energyType.value,
      consumptionPerUnit: undefined,
    };
    open.value = true;
  } else if (type === 'production' || type === 'fabrication_order') {
    const response = standardResponse.value!;
    const item = response?.items?.[index];
    if (!item || !areaId.value || !energyType.value) return;

    modalData.value = {
      start: item.event.production.start,
      end: item.event.production.end,
      type: item.event.production.prod_type === 'fabrication_order' ? 'fabrication_order' : 'production',
      areaId: areaId.value,
      energyType: energyType.value,
      quantity: Number(item.event.production.quantity),
      consumptionPerUnit: Number(item.event.consumption_per_unit.quantity),
    };
    open.value = true;
  }
};

const createDatasets = (standardResponse: StandardResponse | null) => {
  switch (standardResponse?.type) {
    case 'weekly':
      datasets.value = createWeeklyDatasets(standardResponse);
      break;
    case 'off_production':
      datasets.value = createOffProductionDatasets(standardResponse);
      break;
    case 'production':
      datasets.value = createProductionDatasets(standardResponse, 'production');
      break;
    case 'fabrication_order':
      datasets.value = createProductionDatasets(standardResponse, 'fabrication_order');
      break;
    default:
      datasets.value = [];
      break;
  }
};

// === Weekly ===
const createWeeklyDatasets = (data: StandardWeeklyResponse) => {
  if (!data) return [];
  const dataset: BarChartData[] = [];

  data.items.map((item) => {
    dataset.push({
      key: dayjs(item.event.start_date).format('DD/MM/YYYY'),
      barValues: [{ value: item.event.consumption.quantity }],
    });
  });
  return dataset;
};

// === Off Production ===
const createOffProductionDatasets = (data: StandardOffProductionResponse) => {
  if (!data) return [];
  const dataset: BarChartData[] = [];

  data.items.map((item) => {
    dataset.push({
      key: dayjs(item.event.start).format('DD/MM/YYYY HH:mm'),
      barValues: [{ value: item.event.avg_power }],
    });
  });
  return dataset;
};

// === Production ===
const createProductionDatasets = (
  data: StandardProductionResponse | StandardFabricationOrderResponse,
  type: 'production' | 'fabrication_order',
) => {
  if (!data) return [];
  const dataset: BarChartData[] = [];

  data.items.map((item) => {
    dataset.push({
      key: dayjs(item.event.production.start).format(type === 'production' ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm'),
      barValues: [{ value: item.event.consumption_per_unit.quantity }],
    });
  });

  return dataset;
};

watchEffect(async () => {
  loading.value = true;
  await until(loadingStandard).not.toBe(true);
  await createDatasets(standardResponse.value);
  loading.value = false;
});
</script>
<template>
  <nrjx-chart-bar
    v-if="datasets.length > 0 || loading"
    :options="options()"
    :loading="loading"
    :data="datasets"
    clickable
    @click-on-bar="handleClick"
  />
  <div v-else class="flex flex-col items-center justify-center h-[70vh]">
    <div class="flex flex-col items-center justify-center border border-gray-500 bg-gray-100 rounded-md p-4">
      <p v-if="standardError" class="text-gray-500 text-sm">{{ $t(`error.${standardError?.error_code ?? 'title'}`) }}</p>
      <p v-else class="text-gray-500 text-sm">{{ $t('global.no_data_with_this_filters') }}</p>
    </div>
  </div>

  <modale-consumption v-model="open" v-bind="modalData" />
</template>
